import { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addToCart,
  editCartItem,
  removeFromCart,
} from "../../../../redux/cart";
import { uploadImage } from "../../../../services/upload";
import { getAllSkus, getSkuNames } from "../../../../services/sku";
import { searchSkuName } from "../../../../services/indexing";
import { getBase64 } from "../../../../helpers/getBase64";
import { NEW_ITEM_ACTION } from "../../../../constants/global";
import { NEW_ITEM_ACTION_MAP } from "../../../../constants/global";
import Modal from "../Modal";
import UploadedImages from "./UploadedImages/UploadedImages";
import AutoComplete from "../../../AutoComplete/AutoComplete";
import Ripple from "../../spinners/ripple/Ripple";
import classNames from "classnames";
import { AiOutlineCheckCircle } from "react-icons/ai";
import Select from "../../../Select/Select";

const AddItemModal = (props) => {
  const { closeModal, itemPositionToEdit, action } = props;
  const dispatch = useDispatch();
  const selectedCarId = useSelector(
    (state) => state.car.selectedCar.carDetails?.id
  );
  const cart = useSelector((state) => state.cart);
  const { isTaxable } = useSelector((state) => state.order);
  const [quantity, setQuantity] = useState({ value: "", errorMessage: "" });
  const [images, setImages] = useState([]);
  const [imagesError, setImagesError] = useState("");
  const [uploadImagesLoading, setUploadImagesLoading] = useState(false);
  const [itemName, setItemName] = useState("");
  const [selectedSku, setSelectedSku] = useState(null);
  const [skuList, setSkuList] = useState([]);
  const [skuListLoading, setSkuListLoading] = useState(false);
  const [displaySkuOptions, setDisplaySkuOptions] = useState(false);
  const [selectedSkuName, setSelectedSkuName] = useState();
  const [suggestedSkuNamesLoading, setSuggestedSkuNamesLoading] =
    useState(false);
  const [suggestedSkuNames, setSuggestedSkuNames] = useState([]);
  const [skuNamesLoading, setSkuNamesLoading] = useState(false);
  const [skuNames, setSkuNames] = useState([]);
  const uploadInputRef = useRef();
  const isSkuNamesContentVisible =
    isTaxable && !selectedSku && itemName.trim() !== "";

  // handlers
  const quantityChangedHandler = (e) => {
    const { value } = e.target;
    if (value && value.replace(/^0+/, "") !== "") {
      setQuantity({ value: value.replace(/^0+/, ""), errorMessage: "" });
    } else {
      setQuantity({
        value: value.replace(/^0+/, ""),
        errorMessage: "من فضلك ادخل الكمية",
      });
    }
  };
  const uploadImageClickedHandler = () => {
    uploadInputRef.current?.click();
  };
  const imageDeletedHandler = (index) => {
    setImages((prevImages) => prevImages.filter((_, idx) => idx !== index));
  };
  const upload = async (file) => {
    setUploadImagesLoading(true);
    setImagesError("");
    try {
      const base64 = await getBase64(file);
      const response = await uploadImage(base64);
      setImages((prevImages) => [
        ...prevImages,
        { imageUrl: response.data.response },
      ]);
    } catch (error) {
      console.log(error);
      setImagesError("حدث خطا ما برجاء المحاولة مرة اخري");
    }

    setUploadImagesLoading(false);
  };
  const fileSelectedHandler = (event) => {
    const files = event.target.files;
    for (const file of files) {
      if (!file.type.includes("image")) {
        setImagesError("من فضلك اختر صور فقط");
        return;
      } else {
        upload(file);
      }
    }
  };
  const cancelClickedHandler = () => {
    closeModal();
  };
  const skuOptionClickedHandler = (sku) => {
    setSelectedSku(sku);
    setItemName(sku.commercialName);
    setDisplaySkuOptions(false);
  };

  const itemNameChangedHandler = (value) => {
    if (value.trim() === "") {
      setSelectedSku(null);
      setSelectedSkuName(null);
    }
    setItemName(value);
  };

  const itemNameClearedHandler = () => {
    setItemName("");
    setSelectedSku(null);
    setSelectedSkuName(null);
  };

  useEffect(() => {
    let suggestedSkuNamesTimeoutId;
    if (isSkuNamesContentVisible) {
      suggestedSkuNamesTimeoutId = setTimeout(async () => {
        setSuggestedSkuNamesLoading(true);
        try {
          const response = await searchSkuName(itemName);
          const {
            data: { data: suggestedNames },
          } = response;
          setSuggestedSkuNames(suggestedNames);
        } catch (err) {
          console.error(err);
        }
        setSuggestedSkuNamesLoading(false);
      }, 500);
    }

    return () => {
      if (suggestedSkuNamesTimeoutId) {
        clearTimeout(suggestedSkuNamesTimeoutId);
      }
    };
  }, [itemName, isSkuNamesContentVisible]);

  const hideSkuOptionsHandler = () => {
    setDisplaySkuOptions(false);
  };

  const showSkuOptionsHandler = () => {
    setDisplaySkuOptions(true);
  };

  const skuNameSelectedHandler = (skuName) => {
    setSelectedSkuName(skuName);
  };

  useEffect(() => {
    const fetchSkus = async () => {
      const params = {
        carId: selectedCarId,
      };
      setSkuListLoading(true);
      try {
        const response = await getAllSkus(null, params);
        setSkuList(response.data.skuListDetails.skuList);
      } catch (error) {
        console.log(error);
      }
      setSkuListLoading(false);
    };

    if (selectedCarId) {
      fetchSkus();
    }
  }, [selectedCarId]);

  const skusOptions = useMemo(() => {
    if (skuList.length > 0) {
      return skuList
        .reduce((acc, curr, index) => {
          if (
            curr.commercialName
              .toLowerCase()
              .indexOf(itemName.toLowerCase()) !== -1 ||
            curr.id.toString().toLowerCase().indexOf(itemName.toLowerCase()) !==
              -1 ||
            curr.skuBrand.toLowerCase().indexOf(itemName.toLowerCase()) !== -1
          ) {
            return [
              ...acc,
              <div key={curr.id} className="bg-white">
                <div
                  className={classNames(
                    "p-3 hover:bg-gray-200 cursor-pointer flex items-center justify-between",
                    { "bg-gray-200": curr.id === selectedSku?.id }
                  )}
                  tabIndex={0}
                  onClick={() => skuOptionClickedHandler(curr)}
                >
                  <div>
                    <span className="block text-sm font-semibold">
                      الاسم: {curr.commercialName}
                    </span>
                    <span className="block text-sm font-semibold">
                      رقم# {curr.id}
                    </span>
                    <span className="block text-sm font-semibold">
                      الماركة: {curr.skuBrand}
                    </span>
                    <span className="text-sm pl-2 font-semibold">
                      سعر السوق: {curr.marketPrice || "-"}
                    </span>
                    <span className="text-sm font-semibold">
                      سعر المخزن: {curr.inventoryPrice || "-"}
                    </span>
                  </div>

                  {curr.id === selectedSku?.id && (
                    <AiOutlineCheckCircle
                      className="text-end text-green-700"
                      size={20}
                    />
                  )}
                </div>
                {index !== skuList.length - 1 && (
                  <hr className="w-10/12 mx-auto" />
                )}
              </div>,
            ];
          } else {
            return [...acc];
          }
        }, [])
        .slice(0, 400);
    } else {
      return null;
    }
  }, [skuList, itemName, selectedSku]);

  useEffect(() => {
    const fetchSkuNames = async () => {
      setSkuNamesLoading(true);
      try {
        const response = await getSkuNames();
        const {
          data: { data: skuNamesList },
        } = response;
        setSkuNames(skuNamesList);
      } catch (err) {
        console.error(err);
      }
      setSkuNamesLoading(false);
    };
    if (isTaxable) {
      fetchSkuNames();
    }
  }, [isTaxable]);

  // prefill the form for edit
  useEffect(() => {
    if (action === NEW_ITEM_ACTION.EDIT) {
      const cartItem = cart.skus[itemPositionToEdit];
      setQuantity({ value: cartItem.quantity, errorMessage: "" });
      setImages(cartItem.images);
      setItemName(cartItem.name);
    }
  }, [action, itemPositionToEdit, cart]);

  const isSubmitNewItemDisabled = useMemo(() => {
    let isDisabled = false;
    if (
      !quantity.value ||
      typeof parseInt(quantity.value) !== "number" ||
      quantity.value < 1
    ) {
      isDisabled = true;
    } else if (uploadImagesLoading) {
      isDisabled = true;
    } else if (
      isTaxable &&
      (itemName.trim() === "" || (!selectedSku && !selectedSkuName))
    ) {
      isDisabled = true;
    } else if (!isTaxable && itemName.trim() === "" && !selectedSku) {
      isDisabled = true;
    }
    return isDisabled;
  }, [
    quantity,
    uploadImagesLoading,
    itemName,
    selectedSku,
    selectedSkuName,
    isTaxable,
  ]);

  const newItemSubmitHandler = (e) => {
    e.preventDefault();

    if (isSubmitNewItemDisabled) {
      return;
    }

    let newItem;

    if (selectedSku) {
      newItem = {
        ...selectedSku,
        availableQuantity: selectedSku.quantity,
        imageList: [...selectedSku.imageList, ...images],
      };
      newItem.quantity = parseInt(quantity.value);
    } else {
      newItem = {
        imageList: images,
        quantity: parseInt(quantity.value),
        name: itemName,
        skuNameId: selectedSkuName?.id,
      };
    }

    if (action === NEW_ITEM_ACTION.ADD && selectedSku) {
      dispatch(addToCart(newItem));
    } else if (action === NEW_ITEM_ACTION.ADD && !selectedSku) {
      dispatch(addToCart(newItem));
    } else if (action === NEW_ITEM_ACTION.EDIT && selectedSku) {
      dispatch(removeFromCart({ index: itemPositionToEdit }));
    } else {
      dispatch(
        editCartItem({ newItemData: newItem, itemPosition: itemPositionToEdit })
      );
    }

    closeModal();
  };

  return (
    <Modal close={closeModal} classNames="top-4">
      <form
        dir="rtl"
        autoComplete="off"
        className="border-light-gray rounded-lg w-full"
        onSubmit={newItemSubmitHandler}
      >
        <div className="mb-3">
          <AutoComplete
            inputValue={itemName}
            label="اسم القطعة"
            loading={skuListLoading}
            inputPlaceHolder={
              selectedCarId ? "ابحث بالاسم او الرقم او الماركة" : ""
            }
            options={skusOptions}
            onInputChange={itemNameChangedHandler}
            onInputClear={itemNameClearedHandler}
            hideOptions={hideSkuOptionsHandler}
            showOptions={showSkuOptionsHandler}
            isOptionsVisible={displaySkuOptions}
            showInputOnly
          />
        </div>

        {isSkuNamesContentVisible && (
          <>
            <div className="mb-3">
              {suggestedSkuNamesLoading && (
                <div className="text-center">
                  يتم تحميل اسماء القطع المقترحة ...
                  <Ripple className="border-dark-blue" />
                </div>
              )}
              {suggestedSkuNames.length > 0 && (
                <div className="mt-2">
                  <h3 className="mb-2 text-center text-sm font-bold text-dark-blue">
                    ---- اسماء القطع المقترحة ----
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap justify-center">
                    {suggestedSkuNames.map((suggestedSku, index) => (
                      <div
                        key={index}
                        className={classNames("p-2 border rounded-md", {
                          "bg-dark-blue text-white":
                            selectedSkuName?.id === suggestedSku.id,
                        })}
                        role="button"
                        onClick={() => skuNameSelectedHandler(suggestedSku)}
                      >
                        {suggestedSku.skuName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mb-3">
              <p>اختر اسم اخر غير المقترح</p>
              {skuNamesLoading ? (
                <Ripple className="border-dark-blue" />
              ) : (
                <Select
                  placeholder="جميع أسماء القطع المتاحه - ابحث بالاسم"
                  options={skuNames}
                  optionLabelProp="skuName"
                  onSelect={skuNameSelectedHandler}
                  initialValue={selectedSkuName?.skuName}
                />
              )}
            </div>
          </>
        )}

        <div className="mb-3">
          <label htmlFor="quantity">الكمية</label>
          <input
            id="quantity"
            type="number"
            min={1}
            placeholder="الكمية"
            value={quantity.value}
            onChange={quantityChangedHandler}
            className="block w-full border border-light-gray mt-2 p-2 focus:border-dark-blue rounded"
          />
          {quantity.errorMessage && (
            <p className="mt-0 text-xs font-bold text-red-500">
              {quantity.errorMessage}
            </p>
          )}
        </div>
        <div className="mb-3 flex flex-col items-center">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={fileSelectedHandler}
            onClick={(event) => {
              event.target.value = null;
            }}
            className="hidden"
            ref={uploadInputRef}
          />
          <button
            className="mb-2 px-4 py-2 border border-light-gray rounded hover:border-dark-blue focus:border-dark-blue"
            onClick={uploadImageClickedHandler}
            disabled={uploadImagesLoading}
            type="button"
          >
            {uploadImagesLoading ? (
              <Ripple className="border-dark-blue" />
            ) : (
              "اضف صور للقطعة"
            )}
          </button>
          {selectedSku && selectedSku.imageList.length !== 0 && (
            <ul className="flex gap-2 border border-light-gray p-2 rounded">
              {selectedSku.imageList.map((image, index) => (
                <li key={index}>
                  <a href={image.imageUrl} target="_blank" rel="noreferrer">
                    <img
                      src={image.imageUrl}
                      alt="item"
                      className="w-14 h-14 object-center object-cover"
                    />
                  </a>
                </li>
              ))}
            </ul>
          )}
          <UploadedImages images={images} deleteImage={imageDeletedHandler} />
          <p className="text-xs font-bold text-red-500">{imagesError}</p>
        </div>

        <div className="mt-4 pt-4 border-t border-light-gray flex justify-between items-center">
          <button
            type="submit"
            disabled={isSubmitNewItemDisabled}
            className="bg-dark-blue text-white px-4 py-1 rounded-lg disabled:bg-light-gray disabled:cursor-not-allowed"
          >
            {NEW_ITEM_ACTION_MAP.get(action)}
          </button>
          <button
            type="button"
            className="px-4 py-1 rounded-lg border border-light-gray"
            onClick={cancelClickedHandler}
          >
            إلغاء
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddItemModal;
